import React, { useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import {
	Box,
	Typography,
	CircularProgress,
	useMediaQuery,
	Theme,
	useTheme,
	darken,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { DayPickerRangeController } from 'react-dates';
import { OpeningHoursWithExceptions, Duration } from 'common/types';
import { firstDaysOfWeek } from 'common/utils/dateUtils';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization/useTranslation';

import { getOpeningTimesForDateWithMoments } from 'common/utils/shopUtils';

import {
	isTimeSlotWithAvailability,
	AvailabilityByTime,
	ValidSlotsByTime,
} from 'common/modules/availabilities/timeSlots/types';
import { TimeSelectorArgs } from 'components/TimeSelector/types';
import { getPreferredTimeSlotForDate } from './utils';
import { getNewEndTime } from 'common/utils/dateCalculations';

interface Props {
	existingDateSelection: string;
	selectorType: TimeSelectorArgs;
	duration: Duration;
	openingHours: OpeningHoursWithExceptions;
	timeSlots: AvailabilityByTime | ValidSlotsByTime;
	loading: boolean;
	minDate?: moment.Moment;
	setDay: (date: moment.Moment) => void;
}

const DaySelectorCalendar = (props: Props) => {
	const {
		existingDateSelection,
		selectorType,
		duration,
		openingHours,
		timeSlots,
		loading,
		minDate,
		setDay,
	} = props;
	useCustomCalendarStyles();
	const { t } = useTranslation();
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const existingDateMoment = moment(existingDateSelection);
	const visibleMonth = existingDateMoment;
	const isEndTime = selectorType.type === 'endTime';
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const shopTimeZone = useSelector(ShopSelectors.getShopTimeZone);
	const hours = existingDateMoment.hours();
	const minutes = existingDateMoment.minutes();

	const { rangeStart, rangeEnd } = useMemo(() => {
		if (isEndTime) {
			return {
				rangeStart: selectorType.type === 'endTime' ? selectorType.startTime.clone().hour(0) : null,
				rangeEnd: existingDateMoment.clone().hour(0) ?? null,
			};
		} else {
			return {
				rangeStart: existingDateMoment.clone().hour(0) ?? null,
				rangeEnd:
					moment(
						getNewEndTime(existingDateMoment.toISOString(), duration, openingHours, shopTimeZone),
					) ?? null,
			};
		}
	}, [isEndTime, selectorType, existingDateMoment, duration, openingHours, shopTimeZone]);

	const getMinCalendarDate = useCallback((): moment.Moment => {
		const startDayUnix = selectorType.type === 'endTime' ? selectorType.startTime.valueOf() : 0;
		const minDateUnix = minDate && moment(minDate).valueOf();

		return minDateUnix ? moment(Math.max(minDateUnix, startDayUnix)) : moment(startDayUnix);
	}, [minDate, selectorType]);

	const isOutsideRange = useCallback(
		(day: moment.Moment) => {
			const minDate = getMinCalendarDate();
			return minDate.clone().startOf('day').isAfter(day);
		},
		[getMinCalendarDate],
	);

	const isDayFullyBooked = useCallback(
		(day: moment.Moment) => {
			const timeSlotsForDay = timeSlots[day.format('YYYY-MM-DD')];
			if (!timeSlotsForDay) {
				return true;
			}
			const openingTimes = getOpeningTimesForDateWithMoments(day, openingHours);
			if (!openingTimes || openingTimes.isClosed) {
				return true;
			}
			const hours = Object.entries(timeSlotsForDay).filter(
				([hour]) => hour >= openingTimes.openTime && hour <= openingTimes.closeTime,
			);
			if (
				hours.some(([_, validity]) =>
					isTimeSlotWithAvailability(validity) ? validity.units >= 0 : true,
				)
			) {
				return false;
			}
			return true;
		},
		[timeSlots, openingHours],
	);

	const handleNextMonth = useCallback(
		(newMonth: moment.Moment) => {
			const newCurrentDate = newMonth.clone().startOf('month');
			const minCalendarDate = getMinCalendarDate();
			if (newCurrentDate.isBefore(minCalendarDate)) {
				const dateToSet = minCalendarDate.set({ hour: hours, minute: minutes });
				setDay(dateToSet);
			} else {
				const dateToSet = newCurrentDate.set({ hour: hours, minute: minutes });
				setDay(dateToSet);
			}
		},
		[getMinCalendarDate, setDay, hours, minutes],
	);

	const handlePrevMonth = useCallback(
		(newMonth: moment.Moment) => {
			const newCurrentDate = newMonth.clone().endOf('month').startOf('day');
			const minCalendarDate = getMinCalendarDate();
			if (newCurrentDate.isBefore(minCalendarDate)) {
				const dateToSet = minCalendarDate.set({ hour: hours, minute: minutes });
				setDay(dateToSet);
			} else {
				const dateToSet = newCurrentDate.set({ hour: hours, minute: minutes });
				setDay(dateToSet);
			}
		},
		[getMinCalendarDate, setDay, hours, minutes],
	);

	const handleDatesChange = useCallback(
		({
			startDate,
			endDate,
		}: {
			startDate: moment.Moment | null;
			endDate: moment.Moment | null;
		}) => {
			if (isEndTime) {
				const newTimeToSet = getPreferredTimeSlotForDate(
					moment(endDate),
					timeSlots,
					duration,
					openingHours,
					shopTimeZone,
					{
						hours: hours,
						minutes: minutes,
					},
					isEndTime,
					getMinCalendarDate(),
				);
				setDay(newTimeToSet);
			} else {
				const newTimeToSet = getPreferredTimeSlotForDate(
					moment(startDate),
					timeSlots,
					duration,
					openingHours,
					shopTimeZone,
					{
						hours: hours,
						minutes: minutes,
					},
					isEndTime,
					getMinCalendarDate(),
				);
				setDay(newTimeToSet);
			}
		},
		[
			isEndTime,
			timeSlots,
			duration,
			openingHours,
			shopTimeZone,
			hours,
			minutes,
			setDay,
			getMinCalendarDate,
		],
	);

	return (
		<DayPickerRangeController
			startDate={rangeStart ?? null}
			endDate={rangeEnd}
			onDatesChange={handleDatesChange}
			//@ts-expect-error: This property actually exists (in react-dates 21.7.0) but types are broken
			minDate={getMinCalendarDate()}
			minimumNights={0}
			focusedInput={isEndTime ? 'endDate' : 'startDate'}
			onFocusChange={() => {}}
			initialVisibleMonth={() => visibleMonth}
			daySize={isMobile ? 36 : 45}
			onPrevMonthClick={handlePrevMonth}
			onNextMonthClick={handleNextMonth}
			hideKeyboardShortcutsPanel
			isDayHighlighted={isDayFullyBooked}
			isOutsideRange={isOutsideRange}
			firstDayOfWeek={firstDaysOfWeek[shopDateFormat.startDay]}
			renderCalendarInfo={() => (
				<>
					<Box
						mb={2}
						display="flex"
						flexDirection="row"
						alignItems="center"
						justifyContent="center"
						width="100%"
						className={classNames(classes.calendarLoading, {
							[classes.calendarLoadingVisible]: loading,
						})}
					>
						<CircularProgress size={14} />
						<Box mr={1} />
						<Typography align="center" variant="caption">
							{t('timeLocationSelector.loadingAvailabilities', 'Loading availabilities')}
						</Typography>
					</Box>
				</>
			)}
		/>
	);
};

const useCustomCalendarStyles = makeStyles((theme: Theme) =>
	createStyles({
		'@global': {
			'#start-time-selector .DayPicker *': {
				fontFamily: theme.typography.fontFamily,
			},
			'#start-time-selector .CalendarDay__selected': {
				color: `${theme.palette.text.primary} !important`,
				outline: `2px solid ${theme.palette.text.primary}`,
				outlineOffset: '-3px',
				border: `1px solid ${theme.palette.divider}`,
			},
			'#start-time-selector .CalendarDay__default:not(.CalendarDay__blocked_out_of_range)': {
				backgroundColor: theme.palette.background.paper,
				'&:hover': {
					backgroundColor: theme.palette.action.hover,
				},
			},
			'#start-time-selector .CalendarDay.CalendarDay__highlighted_calendar:not(.CalendarDay__blocked_out_of_range)': {
				backgroundColor: theme.palette.colors.availabilityNone.light,
				'&:hover': {
					backgroundColor: darken(theme.palette.colors.availabilityNone.light, 0.2),
				},
			},
			'#start-time-selector .CalendarDay.CalendarDay__selected_span': {
				outline: `1px dashed ${theme.palette.text.primary}`,
				outlineOffset: '-2px',
				borderRadius: '2px',
				border: `1px solid ${theme.palette.divider}`,
				color: theme.palette.text.secondary,
			},
		},
	}),
);

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		calendarWrapper: {
			flex: 1,
			padding: theme.spacing(4),
			paddingTop: theme.spacing(2),
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			background: theme.palette.background.default,
			position: 'relative',
		},
		calendarLoading: {
			transition: 'opacity 0.3s ease',
			opacity: 0,
		},
		calendarLoadingVisible: {
			opacity: 1,
		},
	}),
);

export default DaySelectorCalendar;
